import DOMPurify from 'dompurify';
import { marked } from 'marked';

// Configure marked once for consistent output.
marked.setOptions({ gfm: true, breaks: true });

const BLOCK_ELEMENTS = new Set([
  'p',
  'div',
  'section',
  'article',
  'header',
  'footer',
  'aside',
  'blockquote',
  'pre',
  'ul',
  'ol',
  'table',
  'thead',
  'tbody',
  'tr',
  'h1',
  'h2',
  'h3',
  'h4',
  'h5',
  'h6',
]);

function extractPlainText(node: Node, out: string[]): void {
  if (node.nodeType === Node.TEXT_NODE) {
    out.push(node.textContent ?? '');
    return;
  }

  if (node.nodeType !== Node.ELEMENT_NODE) {
    return;
  }

  const element = node as HTMLElement;
  const tag = element.tagName.toLowerCase();

  if (tag === 'br') {
    out.push('\n');
    return;
  }

  if (tag === 'li') {
    out.push('- ');
  }

  for (const child of Array.from(element.childNodes)) {
    extractPlainText(child, out);
  }

  if (tag === 'li') {
    out.push('\n');
    return;
  }

  if (BLOCK_ELEMENTS.has(tag)) {
    out.push('\n');
  }
}

/**
 * Convert markdown text into sanitized HTML ready for rendering.
 */
export function renderMarkdown(markdown: string): string {
  if (!markdown.trim()) {
    return '';
  }

  const rendered = marked.parse(markdown, { async: false });
  const html = typeof rendered === 'string' ? rendered : '';

  return DOMPurify.sanitize(html, { USE_PROFILES: { html: true } });
}

/**
 * Convert markdown to plain text while preserving readable line breaks.
 */
export function markdownToPlainText(markdown: string): string {
  if (!markdown.trim()) {
    return '';
  }

  const html = renderMarkdown(markdown);
  if (!html) {
    return '';
  }

  const doc = new DOMParser().parseFromString(html, 'text/html');
  const chunks: string[] = [];

  for (const child of Array.from(doc.body.childNodes)) {
    extractPlainText(child, chunks);
  }

  return chunks
    .join('')
    .replaceAll('\u00a0', ' ')
    .replace(/[ \t]+\n/g, '\n')
    .replace(/\n{3,}/g, '\n\n')
    .trim();
}
